<template>
  <help-dialog title="Code Generation" maxWidth="800px">
    <template #default>
      <h3 class="mb-2 text-black">
        The Domain-Centric View
      </h3>
      <p class="text-grey-darken-3">
        The left side displays a unique file tree that directly mirrors your Domain Model, organized by domain concepts
        rather than traditional folder structures:
      </p>
      <ul class="pl-10">
        <li class="text-grey-darken-3"><b>Commands</b> (e.g., <code>create-todo</code>, <code>update-todo</code>)</li>
        <li class="text-grey-darken-3"><b>Entities</b> (e.g., <code>todo</code>)</li>
        <li class="text-grey-darken-3"><b>GWTs</b> (your Given-When-Then test scenarios)</li>
        <li class="text-grey-darken-3"><b>Read Models</b> (e.g., <code>get-all-todos</code>)</li>
      </ul>
      <br/>
      <p class="text-grey-darken-3">
        This view provides instant feedback with status indicators:
      </p>
      <ul class="pl-10">
        <li class="text-grey-darken-3">
          <b>Red dot:</b> The underlying domain element has changed since the code was last generated. Time to update!
        </li>
        <li class="text-grey-darken-3">
          <b>Yellow dot:</b> The AI has generated changes that haven't been approved yet.
        </li>
      </ul>
      <br/>
      <p class="text-grey-darken-3">
        This closes the loop between your design and the actual code, ensuring your documentation is always aligned with your codebase.
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Code Generation Steps
      </h3>

      <p class="text-grey-darken-3">
        <b>Step 1: Validate Domain Model</b>
      </p>
      <p class="text-grey-darken-3 pl-5">
        Before generating code, validate your Domain Model to catch any issues. The validator checks for
        missing configurations such as unlinked entities, missing aggregate roots, and other potential problems.
        Resolve all errors and warnings to ensure the best code generation results.
      </p>
      <br/>

      <p class="text-grey-darken-3">
        <b>Step 2: Choose a Framework Boilerplate</b>
      </p>
      <p class="text-grey-darken-3 pl-5">
        Select a <b>Starter Template</b> from the Code Generation Steps panel. Think of this as a professional,
        full-stack framework boilerplate for your project. Currently available: JavaScript with Express backend
        and TypeScript/React frontend. More templates (NestJS, FastAPI, Spring Boot, ASP.NET, Fiber, etc.)
        are being added continuously.
      </p>
      <br/>

      <p class="text-grey-darken-3">
        <b>Step 3: Review the API Specification</b>
      </p>
      <p class="text-grey-darken-3 pl-5">
        Continuum Design automatically translates your Domain Model into a standard <b>OpenAPI (Swagger) specification</b>.
        This details all endpoints and request/response models. Review this specification to ensure the AI will
        build exactly what you designed.
      </p>
      <br/>

      <p class="text-grey-darken-3">
        <b>Step 4: Generate Code with AI</b>
      </p>
      <p class="text-grey-darken-3 pl-5">
        Choose between two generation modes:
      </p>
      <ul class="pl-10">
        <li class="text-grey-darken-3">
          <b>Generate All:</b> Click <b>Generate/Update API</b> to process all changes (items with red dots).
          Generates backend API endpoints for Commands, data models for Entities, query handlers for Read Models,
          business logic and unit tests from GWT scenarios, and a complete React frontend with auto-wired forms.
          The system only changes parts of your codebase directly affected by domain changes.
        </li>
        <li class="text-grey-darken-3">
          <b>Generate Per-Item:</b> For fine-grained control, click the menu next to any command, entity, read model,
          or GWT and select "Generate" or "Regenerate" to update only that specific item.
        </li>
      </ul>
      <br/>

      <p class="text-grey-darken-3">
        <b>Step 5: Save Changes</b>
      </p>
      <p class="text-grey-darken-3 pl-5">
        The AI's work is presented as a <b>diff</b>, similar to a Git pull request. View original code and AI suggestions
        side-by-side. Left shows current state, right shows proposed changes in green. You can
        <b>approve changes for individual files</b> by clicking "Apply" or click <b>"Approve all changes"</b>
        to accept all suggestions at once. You are always the final authority.
      </p>
      <br/>

      <p class="text-grey-darken-3">
        <b>Step 6: Run Tests</b>
      </p>
      <p class="text-grey-darken-3 pl-5">
        Click <b>Run Tests</b> to execute the test suite automatically created from your GWT scenarios. Continuum Design spins up
        a secure, isolated environment, builds your project, installs dependencies, and runs tests. View real-time output
        to confirm your application's business logic is sound.
      </p>
      <br/>

      <p class="text-grey-darken-3">
        <b>Step 7: Commit and Push to GitHub</b>
      </p>
      <p class="text-grey-darken-3 pl-5">
        Navigate to the <b>GitHub Tab</b> to connect your project to a repository, view a diff of approved changes,
        write a commit message, and commit/push directly to your branch. Alternatively, download the entire project
        as a ZIP file from the Code View tab using the Download button.
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Advanced Controls
      </h3>
      <ul class="pl-10">
        <li class="text-grey-darken-3">
          <b>Selecting Your AI Model:</b> Choose the specific Large Language Model (LLM) for code generation.
          The AI model selector is located at the bottom of the Code Generation Steps panel.
        </li>
        <li class="text-grey-darken-3">
          <b>Resetting Your Code View:</b> The <b>Reset Code View</b> button permanently deletes all generated
          code within this Bounded Context in Continuum Design, unlinks the GitHub repository, and allows you to select
          a new Starter Template. This does not affect code on GitHub.
        </li>
      </ul>

      <br/>
      <p class="text-grey-darken-4">
        For a complete walkthrough with detailed examples and screenshots,
        <a
          href="https://www.qlerify.com/ai-generated-code"
          target="_blank"
          class="text-blue-accent-4"
        >
          visit our full guide
        </a>.
      </p>
    </template>
  </help-dialog>
</template>

<script setup>
import HelpDialog from '@/components/HelpDialog.vue'
</script>
